import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import styled from "styled-components";
import { FaCheckCircle, FaSearch, FaTimesCircle } from "react-icons/fa";
import AdminNavbar from "./AdminNavbar";

const API_URL = "https://server229256.nazwa.pl/api/admin/basic-demo-accesses";
const formatDate = (value) => value
  ? new Date(value).toLocaleString("pl-PL")
  : "—";

const ConsentIcon = ({ accepted }) => accepted
  ? <FaCheckCircle color="#16a34a" title="Zaakceptowano" />
  : <FaTimesCircle color="#dc2626" title="Nie zaakceptowano" />;

export default function AdminBasicDemoAccesses() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.get(API_URL, {
        headers: { Authorization: localStorage.getItem("adminToken") },
        params: { page, limit: 50, search: query || undefined },
      });
      setRecords(Array.isArray(data?.records) ? data.records : []);
      setPagination(data?.pagination || { page: 1, pages: 1, total: 0 });
    } catch (err) {
      setError(err?.response?.data?.error || "Nie udało się pobrać historii dostępów.");
    } finally {
      setLoading(false);
    }
  }, [page, query]);

  useEffect(() => { load(); }, [load]);

  const submitSearch = (event) => {
    event.preventDefault();
    setPage(1);
    setQuery(search.trim());
  };

  return (
    <Page>
      <AdminNavbar />
      <Content>
        <Header>
          <div>
            <h1>Dostępy BASIC DEMO</h1>
            <p>Zarejestrowane wejścia i akceptacje użytkowników wersji demo.</p>
          </div>
          <Counter>{pagination.total} wpisów</Counter>
        </Header>

        <SearchForm onSubmit={submitSearch}>
          <FaSearch />
          <input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Szukaj po e-mailu, firmie, koncie lub IP" />
          <button type="submit">Szukaj</button>
        </SearchForm>

        {error && <ErrorBox>{error}</ErrorBox>}
        <TableCard>
          {loading ? <StateBox>Ładowanie danych…</StateBox> : records.length === 0
            ? <StateBox>Brak zapisanych dostępów BASIC DEMO.</StateBox>
            : <TableWrap><Table>
                <thead><tr>
                  <th>Data i czas</th><th>E-mail</th><th>Konto</th><th>Firma</th>
                  <th>IP</th><th>Regulamin</th><th>RODO</th><th>Przeglądarka</th>
                </tr></thead>
                <tbody>{records.map((row) => <tr key={row._id}>
                  <td>{formatDate(row.accessedAt)}</td>
                  <td><Email>{row.email}</Email></td>
                  <td>{row.demoUserID || "—"}</td><td>{row.company || "—"}</td>
                  <td>{row.ipAddress || "—"}</td>
                  <td className="consent"><ConsentIcon accepted={row.legalAccepted} /></td>
                  <td className="consent"><ConsentIcon accepted={row.rodoAccepted} /></td>
                  <td><Agent title={row.userAgent}>{row.userAgent || "—"}</Agent></td>
                </tr>)}</tbody>
              </Table></TableWrap>}
        </TableCard>

        <Pagination>
          <button disabled={page <= 1 || loading} onClick={() => setPage((p) => p - 1)}>← Poprzednia</button>
          <span>Strona {pagination.page} z {pagination.pages}</span>
          <button disabled={page >= pagination.pages || loading} onClick={() => setPage((p) => p + 1)}>Następna →</button>
        </Pagination>
      </Content>
    </Page>
  );
}

const Page = styled.div`min-height:100vh;background:#f3f6f9;font-family:"Inter",sans-serif;`;
const Content = styled.main`max-width:1500px;margin:auto;padding:36px 24px 60px;`;
const Header = styled.div`
  display:flex;justify-content:space-between;gap:20px;margin-bottom:24px;
  h1{margin:0 0 8px;color:#172033} p{margin:0;color:#64748b}
`;
const Counter = styled.span`height:fit-content;padding:8px 13px;border-radius:999px;background:#dff8fa;color:#11747b;font-weight:800;`;
const SearchForm = styled.form`
  display:flex;align-items:center;gap:10px;margin-bottom:18px;padding:10px 12px;
  border:1px solid #dbe3eb;border-radius:12px;background:#fff;color:#64748b;
  input{flex:1;border:0;outline:0;padding:6px;font:inherit}
  button{border:0;border-radius:8px;padding:9px 18px;background:#009fb7;color:#fff;font-weight:800;cursor:pointer}
`;
const TableCard = styled.section`border-radius:14px;background:#fff;box-shadow:0 5px 20px rgba(15,23,42,.08);overflow:hidden;`;
const TableWrap = styled.div`overflow-x:auto;`;
const Table = styled.table`
  width:100%;border-collapse:collapse;
  th,td{padding:13px 14px;border-bottom:1px solid #edf1f5;text-align:left;vertical-align:middle}
  th{background:#f7f9fb;color:#475569;font-size:.78rem;text-transform:uppercase;white-space:nowrap}
  td{color:#334155;font-size:.9rem} tbody tr:hover{background:#f8fcfd}.consent{text-align:center;font-size:1.15rem}
`;
const Email = styled.span`font-weight:800;color:#11747b;`;
const Agent = styled.span`display:block;max-width:260px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;`;
const StateBox = styled.div`padding:50px 20px;text-align:center;color:#64748b;`;
const ErrorBox = styled.div`margin-bottom:18px;padding:12px 15px;border-radius:10px;background:#fee2e2;color:#991b1b;font-weight:700;`;
const Pagination = styled.div`
  display:flex;justify-content:center;align-items:center;gap:16px;margin-top:22px;color:#475569;
  button{border:1px solid #cbd5e1;border-radius:8px;padding:9px 14px;background:#fff;color:#334155;font-weight:700;cursor:pointer}
  button:disabled{opacity:.45;cursor:not-allowed}
`;
